import type * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import type { Product } from '@etiquetachef/types';
import { formatDateTime } from '@/lib/format';
import { groupProductsByCategory } from '@/features/products/categoryGroups';
import { useProducts } from '@/features/products/useProducts';

type Props = {
  tenantId: string;
};

/**
 * Aba de produtos do tenant (admin, Cloudscape). Read-only: o admin apenas
 * observa o catálogo — toda mutação acontece no app cliente / Android. Os
 * produtos são agrupados por categoria (uma `ExpandableSection` por categoria,
 * ordem alfabética pelo label traduzido, "OUTROS" por último), espelhando
 * `ProductsPage`/`ProductList` de `apps/app` e o `ProductsScreen` do Android.
 *
 * Origem: sem issue — commit 92e8a3839, 2026-06-10 17:53 +0200.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Produtos).
 *
 * Dados: `tenants/{tenantId}/products` (`useProducts`), agrupado por
 * `groupProductsByCategory`.
 */
export function ProductsTab({ tenantId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const result = useProducts(tenantId);

  const groups = useMemo(
    () => (result.kind === 'READY' ? groupProductsByCategory(result.products, t) : []),
    [result, t],
  );

  if (result.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (result.kind === 'ERROR') {
    return <Alert type="error">{t('adminProducts.errorLoad')}</Alert>;
  }

  const columns: TableProps.ColumnDefinition<Product>[] = [
    {
      id: 'name',
      header: t('adminProducts.columns.name'),
      cell: (p): React.ReactNode => <Box fontWeight="bold">{p.name}</Box>,
      isRowHeader: true,
    },
    {
      id: 'sku',
      header: t('adminProducts.columns.sku'),
      cell: (p): React.ReactNode =>
        p.sku ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.sku}</span> : '—',
    },
    {
      id: 'unit',
      header: t('adminProducts.columns.unit'),
      cell: (p): React.ReactNode => t(`app.products.unitOptions.${p.unit}`),
    },
    {
      id: 'validityRules',
      header: t('adminProducts.columns.validityRules'),
      cell: (p): React.ReactNode => p.validityRules.length,
    },
    {
      id: 'allergens',
      header: t('adminProducts.columns.allergens'),
      cell: (p): React.ReactNode => (p.allergens.length > 0 ? p.allergens.length : '—'),
    },
    {
      id: 'minStock',
      header: t('adminProducts.columns.minStock'),
      cell: (p): React.ReactNode => p.minStock ?? '—',
    },
    {
      id: 'status',
      header: t('adminProducts.columns.status'),
      cell: (p): React.ReactNode =>
        p.active ? (
          <Badge color="green">{t('adminProducts.badge.active')}</Badge>
        ) : (
          <Badge color="grey">{t('adminProducts.badge.archived')}</Badge>
        ),
    },
    {
      id: 'updatedAt',
      header: t('adminProducts.columns.updatedAt'),
      cell: (p): React.ReactNode => (p.updatedAt != null ? formatDateTime(p.updatedAt, locale) : '—'),
    },
  ];

  return (
    <SpaceBetween size="m">
      <Header variant="h2" description={t('adminProducts.description')}>
        {t('adminProducts.title')}
      </Header>

      {groups.length === 0 ? (
        <Container>
          <Box textAlign="center" padding="m" color="text-body-secondary">
            {t('adminProducts.empty')}
          </Box>
        </Container>
      ) : (
        groups.map((group) => (
          <ExpandableSection
            key={group.category}
            variant="container"
            headerText={group.label}
            headerCounter={`(${String(group.items.length)})`}
          >
            <Table<Product>
              items={group.items}
              trackBy="id"
              variant="embedded"
              columnDefinitions={columns}
            />
          </ExpandableSection>
        ))
      )}
    </SpaceBetween>
  );
}
